import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { ApiError } from '../../services/api';
import { getMyFeatureFlags } from '../../services/feature-flags';
import type { FeatureFlagKey } from '../../types/featureFlags';
import { FeatureFlagsContext, type FeatureFlagsContextValue } from './featureFlagsContext';

const NO_FLAGS: ReadonlySet<string> = new Set();

interface LoadedFlags {
  /** Whose list this is, so a new sign-in never sees the previous user's flags. */
  scope: string;
  enabled: ReadonlySet<string>;
}

/**
 * Loads `GET api/feature-flags/me` once per signed-in user and organization, and hands the answer
 * to `useFeatureFlags`.
 *
 * Fails closed. A 404 (an API that predates flags, which is ordinary during a deploy) or any other
 * error on the first load leaves every flag off, so a dark feature stays dark rather than the page
 * breaking. A failed refresh keeps the list already loaded for the same scope.
 */
export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { user, effectiveOrganizationId } = useAuth();
  const userId = user?.profile?.id ?? null;
  const scope = userId ? `${userId}|${effectiveOrganizationId ?? ''}` : null;
  const [loaded, setLoaded] = useState<LoadedFlags | null>(null);
  // Bumped each time a load lands, so readers of other flag lists know to ask again.
  const [revision, setRevision] = useState(0);
  // Bumped by every load and by a change of user, so an answer that arrives late is dropped.
  const requestId = useRef(0);

  const load = useCallback(async (forScope: string) => {
    const id = ++requestId.current;
    let answer: ReadonlySet<string> | null = null;
    try {
      answer = new Set((await getMyFeatureFlags()).enabled);
    } catch (error) {
      if (!(error instanceof ApiError && error.statusCode === 404)) {
        console.warn('Feature flags are unavailable:', error);
      }
    }
    if (id !== requestId.current) return;
    // A failed refresh keeps the last good list for the same user and organization. Only a scope
    // with no answer yet fails closed, so one flaky request never hides a live feature.
    setLoaded((prev) => ({
      scope: forScope,
      enabled: answer ?? (prev && prev.scope === forScope ? prev.enabled : NO_FLAGS),
    }));
    setRevision((r) => r + 1);
  }, []);

  useEffect(() => {
    if (!scope) return;
    void load(scope);
    return () => {
      requestId.current += 1;
    };
  }, [scope, load]);

  const refresh = useCallback(() => (scope ? load(scope) : Promise.resolve()), [scope, load]);

  // Derived during render: a list loaded for someone else is never served, even for one frame.
  const current = loaded && loaded.scope === scope ? loaded : null;
  const status = scope === null || current ? 'ready' : 'loading';
  const enabled = current?.enabled ?? NO_FLAGS;

  const isEnabled = useCallback((key: FeatureFlagKey) => enabled.has(key), [enabled]);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ status, isEnabled, refresh, revision }),
    [status, isEnabled, refresh, revision]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};
